import { useEffect, useMemo } from 'react';
import { connect, useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { Button, ButtonLink, LoadingTable } from '../../../components/base';
import { ConfirmModal } from '../../../components/common/ConfirmModal';
import { DataTable, IDataTableColumn } from '../../../components/common/DataTable';
import { BlockCard, PageBody, PageContainer, PageHeader } from '../../../components/container';
import { BadgesRoleScopeName } from '../../../components/shared/system';
import { BaseShowDetail } from '../../../context/base/BaseShowDetail';
import { eBaseActionStatus } from '../../../context/base/eBaseActionStatus';
import { RoleModel, RoleResponse } from '../../../context/models/roles/RoleModel';
import { RoleScopeModel } from '../../../context/models/roles/RoleScopeModel';
import { RolesActionTypes } from '../../../context/stores/roles/IRolesActionsTypes';
import { actions } from '../../../context/stores/rootActions';
import { AppState } from '../../../context/stores/rootReducers';
import RoleDetailModal from './detail-modal';

type IRolesPageProps = {
	titlePage: string;
	status: eBaseActionStatus;
	allDatas: RoleResponse[];
	allRoleScopes: RoleScopeModel[];
	selectedIds: number[];
	showDetail: BaseShowDetail<RoleModel>;
	showConfirm: boolean;
	handleReloadAllData: () => void;
	handleSaveData: (data: RoleModel) => void;
	handleDeleteData: (data: number[]) => void;
	onChangeSelectedIds: (ids: number[]) => void;
	onShowDetail: (data: BaseShowDetail<RoleModel>) => void;
	onShowConfirm: (isShow: boolean) => void;
};

const mapStateToProps = (state: AppState) => {
	return {
		titlePage: `Tác nhân`,
		status: state.roles.status,
		allDatas: state.roles.allRoles,
		allRoleScopes: state.roleScopes.allRoleScopes,
		selectedIds: state.roles.selectedIds,
		showDetail: state.roles.showDetail,
		showConfirm: state.roles.showConfirm,
	};
};

const mapDispatchToProps = (dispatch: Dispatch<RolesActionTypes>) => ({
	handleReloadAllData: () => {
		dispatch(actions.roles.getAllRequest());
	},
	handleSaveData: (data: RoleModel) => {
		dispatch(actions.roles.saveRequest(data));
	},
	handleDeleteData: (ids: number[]) => {
		dispatch(actions.roles.deleteRequest(ids));
	},
	onChangeSelectedIds: (ids: number[]) => {
		dispatch(actions.roles.changeSelectedIds(ids));
	},
	onShowDetail: (data: BaseShowDetail<RoleModel>) => {
		dispatch(actions.roles.showDetail(data));
	},
	onShowConfirm: (isShow: boolean) => {
		dispatch(actions.roles.showConfirm(isShow));
	},
});

const RolesPage = (props: IRolesPageProps) => {
	const dispatch = useDispatch();
	const {
		titlePage,
		status,
		selectedIds,
		showConfirm,
		showDetail,
		allDatas,
		allRoleScopes,
		handleReloadAllData,
		onChangeSelectedIds,
		handleDeleteData,
		handleSaveData,
		onShowDetail,
		onShowConfirm,
	} = props;

	useEffect(() => {
		handleReloadAllData();
		if (allRoleScopes.length === 0) dispatch(actions.roleScopes.getAllRequest());
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (status === eBaseActionStatus.reload) handleReloadAllData();
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [status]);

	const calculateFilterExpression = (filterValue: any, selectedFilterOperation: any, target: any) => {
		if (target === 'search' && typeof filterValue === 'string') {
			return [target.dataField, 'contains', filterValue];
		}
		return function (data: any) {
			return (data.roleScopes || []).indexOf(filterValue) !== -1;
		};
	};

	const columns = useMemo((): IDataTableColumn<RoleResponse>[] => {
		return [
			{ accessorKey: 'name', header: 'Tên', size: 150 },
			{ accessorKey: 'description', header: 'Mô tả', minSize: 150 },
			{
				accessorKey: 'roleScopes',
				header: 'Quyền hạn',
				size: 250,
				cell: ({ getValue }) => {
					const val = getValue() as string[];
					return <BadgesRoleScopeName value={val} />;
				}
			},
			{
				accessorKey: 'isActive',
				header: 'Áp dụng',
				size: 80,
				cell: ({ getValue }) => {
					const val = getValue() as boolean;
					return <span className={val ? "text-primary/90 font-medium" : "text-muted-foreground"}>{val ? 'Áp dụng' : 'Không'}</span>;
				}
			},
			{
				accessorKey: 'isProtected',
				header: 'Không sửa đổi',
				size: 80,
				cell: ({ getValue }) => {
					const val = getValue() as boolean;
					return <span className={val ? "text-muted-foreground" : "text-primary/80"}>{val ? 'Khóa' : 'Có thể sửa'}</span>;
				}
			},
			{
				id: 'actions',
				header: 'Sửa',
				meta: { align: 'center' },
				size: 60,
				cell: ({ row }) => !row.original.isProtected ? (
					<ButtonLink onClick={() => onShowDetail({ isShow: true, detailData: row.original })} title='Sửa' icon='ni ni-edit' theme='link' />
				) : null
			}
		];
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [allRoleScopes]);

	return (
		<>
			<PageContainer>
				<PageHeader title={`Danh sách ${titlePage}`}>
					<div className='flex flex-wrap items-center justify-end'>
						<ul className='flex flex-wrap items-center gap-2 [&>li]:list-none'>
							<li>
								<Button
									text={`Xóa (${selectedIds.length})`}
									icon='icon ni ni-trash'
									className='hidden md:inline-flex'
									theme='danger'
									isDisabled={selectedIds.length <= 0}
									onClick={() => onShowConfirm(true)}
								/>
							</li>

							<li>
								<Button
									text='Tạo mới'
									icon='icon ni ni-plus'
									theme='primary'
									className='hidden md:inline-flex'
									onClick={() => onShowDetail({ isShow: true })}
								/>
							</li>
						</ul>
					</div>
				</PageHeader>
				<PageBody>
					<BlockCard>
						{status === eBaseActionStatus.loading && <LoadingTable />}
						{status !== eBaseActionStatus.loading && (
							<DataTable
								data={allDatas}
								columns={columns}
								isLoading={status !== eBaseActionStatus.complete}
								enableRowSelection
								rowSelection={selectedIds.reduce((acc, id) => ({ ...acc, [String(id)]: true }), {})}
								onRowSelectionChange={(selection) => {
									onChangeSelectedIds(Object.keys(selection).filter(k => selection[k]).map(Number));
								}}
								enablePagination
								totalRecords={allDatas.length}
								enableGlobalFilter
								toolbar={{
									isShowSearch: true,
									searchPlaceholder: `Tìm kiếm...`,
									extraActions: <span className='text-sm text-muted-foreground'>Tổng: <b>{allDatas.length}</b></span>
								}}
								onRefresh={handleReloadAllData}
							/>
						)}
					</BlockCard>
				</PageBody>
			</PageContainer>
			{showDetail && showDetail.isShow && (
				<>
					<RoleDetailModal
						isSaving={status === eBaseActionStatus.saving}
						isShow={showDetail.isShow}
						detailModel={showDetail.detailData}
						onClose={() => onShowDetail({ isShow: false })}
						onSubmit={(data) => handleSaveData(data)}
						title={`${showDetail.detailData ? `Cập nhật` : `Thêm mới`} ${titlePage}`}
					/>
				</>
			)}
			{showConfirm && (
				<>
					<ConfirmModal
						show={showConfirm}
						innerText={`Bạn chắc chắn muốn xóa ${selectedIds.length} ${titlePage.toLowerCase()} đã chọn ?`}
						type='danger'
						onClose={() => onShowConfirm(false)}
						onConfirmed={() => handleDeleteData(selectedIds)}
						isLoading={status === eBaseActionStatus.loading || status === eBaseActionStatus.saving}
						btnConfirmText='Xóa'
						btnCloseText='Không xóa'
					/>
				</>
			)}
		</>
	);
};

export default connect(mapStateToProps, mapDispatchToProps)(RolesPage);
